import { useEffect, useState } from "react";
import api from "@/lib/api";
import { StarDisplay } from "@/components/Stars";

const VENDOR_LABELS = {
  punctuality: "Punctuality",
  communication: "Communication",
  cleanliness: "Cleanliness",
  booth: "Booth",
  product: "Product",
};

const ORGANIZER_LABELS = {
  ease_of_finding: "Ease of locating",
  attendees: "Attendees",
  sales: "Sales",
  location: "Location",
  parking: "Parking",
  organizer_communication: "Organizer communication",
  // Legacy keys — kept so old aggregate payloads still render neatly
  coordination: "Coordination",
  venue: "Venue & placement",
  communication: "Communication",
};

function timeAgo(iso) {
  if (!iso) return "";
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 2592000) return `${Math.floor(secs / 86400)}d ago`;
  return `${Math.floor(secs / 2592000)}mo ago`;
}

export function RatingSummaryInline({ summary, selfHidden }) {
  if (selfHidden) {
    return (
      <span className="text-xs italic" style={{ color: "var(--ink-soft)" }} data-testid="rating-summary-self-hidden">
        Your ratings are hidden from you
      </span>
    );
  }
  if (!summary || summary.count === 0) {
    return (
      <span className="text-xs" style={{ color: "var(--ink-soft)" }} data-testid="rating-summary-empty">
        No ratings yet
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2" data-testid="rating-summary-inline">
      <StarDisplay value={summary.overall} size={14} />
      <span className="text-sm font-medium">{summary.overall.toFixed(1)}</span>
      <span className="text-xs" style={{ color: "var(--ink-soft)" }}>({summary.count})</span>
    </span>
  );
}

export default function RatingsPanel({ userId, role, initialSummary, initialSelfHidden }) {
  const [data, setData] = useState(
    initialSelfHidden
      ? { self_hidden: true, summary: null, recent: [] }
      : initialSummary
      ? { summary: initialSummary, recent: null, self_hidden: false }
      : null
  );
  const [loading, setLoading] = useState(!initialSummary && !initialSelfHidden);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`/ratings/user/${userId}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="text-sm" style={{ color: "var(--ink-soft)" }}>Loading ratings…</div>;
  if (!data) return null;
  if (data.self_hidden) {
    return (
      <div
        data-testid="ratings-self-hidden"
        className="card-warm rounded-sm p-8 text-center"
        style={{ color: "var(--ink-soft)" }}
      >
        <div className="font-serif text-2xl" style={{ color: "var(--ink)" }}>Your ratings are private to you.</div>
        <p className="mt-3 text-sm max-w-md mx-auto leading-relaxed">
          AddVendTour keeps the ratings you receive hidden from you on purpose — it protects the honesty of
          community feedback and stops any retaliation against raters. Organizers and vendors who visit
          your profile will still see your public score and comments.
        </p>
      </div>
    );
  }
  const labels = role === "vendor" ? VENDOR_LABELS : ORGANIZER_LABELS;
  const { summary, recent } = data;

  return (
    <div className="space-y-8" data-testid="ratings-panel">
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <div className="overline" style={{ color: "var(--moss)" }}>Overall</div>
          {summary.count > 0 ? (
            <>
              <div className="mt-3 flex items-baseline gap-3">
                <div className="font-serif text-6xl leading-none" data-testid="ratings-overall">
                  {summary.overall.toFixed(1)}
                </div>
                <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                  /5 · {summary.count} {summary.count === 1 ? "rating" : "ratings"}
                </div>
              </div>
              <div className="mt-2">
                <StarDisplay value={summary.overall} size={20} />
              </div>
            </>
          ) : (
            <div className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}
                 data-testid="ratings-overall-empty">
              No ratings yet — be the first to leave one.
            </div>
          )}
        </div>
        <div>
          <div className="overline" style={{ color: "var(--moss)" }}>By category</div>
          {summary.count > 0 ? (
            <div className="mt-3 space-y-2">
              {Object.entries(labels).map(([k, label]) => {
                const v = summary.dimensions?.[k] || 0;
                return (
                  <div key={k} className="flex items-center justify-between gap-4" data-testid={`ratings-dim-${k}`}>
                    <span className="text-sm">{label}</span>
                    <span className="inline-flex items-center gap-2">
                      <StarDisplay value={v} size={14} />
                      <span className="text-xs w-8 text-right" style={{ color: "var(--ink-soft)" }}>
                        {v > 0 ? v.toFixed(1) : "—"}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}
                 data-testid="ratings-dim-empty">
              Category breakdown will appear here after the first rating.
            </div>
          )}
        </div>
      </div>

      {recent && recent.length > 0 && (
        <div>
          <div className="overline" style={{ color: "var(--moss)" }}>Recent comments</div>
          <div className="mt-4 space-y-4" data-testid="ratings-recent">
            {recent.filter((r) => r.comment).slice(0, 10).map((r) => (
              <div key={r.id} className="card-warm rounded-sm p-4" data-testid={`rating-item-${r.id}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm font-medium">{r.from_name}</div>
                  <span className="inline-flex items-center gap-1">
                    <StarDisplay value={r.overall} size={12} />
                    <span className="text-xs">{r.overall.toFixed(1)}</span>
                  </span>
                </div>
                <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                  {r.event_title} · {timeAgo(r.created_at)}
                </div>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>{r.comment}</p>
              </div>
            ))}
            {recent.filter((r) => r.comment).length === 0 && (
              <div className="text-sm" style={{ color: "var(--ink-soft)" }}>No comments yet.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
